import { useState, useEffect } from 'react';
import { storageBucket } from './firebase';
import { apiPost } from './api';

// Renditions are fetched over a public capability URL rather than a signed one.
//
// A signed URL carries a per-user expiry and signature, so every member gets a
// different URL for the same 128px thumbnail and neither the browser cache nor
// Hosting's CDN can reuse anything — and it costs a `get_signed_url` round trip
// per image, into a function that scales to zero and cold-starts on the critical
// path of every avatar. A 130-person tree made 130 of those calls. The token is
// written on the object at upload time (api/thumbnails.py), so the URL can be
// built here with no server call at all.
//
// The trade is stated in full in api/thumbnails.py: the URL *is* the credential,
// and it stays valid until the token is rotated. It applies to the derivative
// only — originals are still signed, below.
function renditionUrl(path, token) {
  if (!storageBucket || !path || !token) return null;
  // encodeURIComponent escapes the slashes too, which is what this endpoint
  // wants: the whole object path is one path segment after /o/.
  return `https://firebasestorage.googleapis.com/v0/b/${storageBucket}`
    + `/o/${encodeURIComponent(path)}?alt=media&token=${token}`;
}

// Module-level cache: storagePath → { url, expiresAt }
const cache = new Map();
// storagePath → in-flight request, so two components asking for the same object
// at once make one call rather than two. The result cache only helps once a
// request has come back, and a mount storm is exactly when several arrive
// together.
const pending = new Map();

// Signing is a route on the tree, so the tree has to be named. It is already in
// the path — every object lives under trees/{treeId}/ — and the server checks
// the two agree rather than trusting this.
function treeIdFromPath(storagePath) {
  const match = /^trees\/([^/]+)\//.exec(storagePath || '');
  return match ? match[1] : null;
}

export async function getSignedUrl(storagePath) {
  if (!storagePath) return null;
  const cached = cache.get(storagePath);
  if (cached && Date.now() < cached.expiresAt) return cached.url;
  if (pending.has(storagePath)) return pending.get(storagePath);

  const treeId = treeIdFromPath(storagePath);
  if (!treeId) return null;

  const request = apiPost(
    `/api/trees/${encodeURIComponent(treeId)}/signed-url`,
    { storagePath },
  ).then(({ url }) => {
    // Cache for 55 minutes (signed URL TTL is 60 min)
    cache.set(storagePath, { url, expiresAt: Date.now() + 55 * 60 * 1000 });
    return url;
  }).finally(() => {
    pending.delete(storagePath);
  });

  pending.set(storagePath, request);
  return request;
}

// Picks which stored object to render at a given display size.
//
// The server keeps 128px and 512px renditions of every uploaded image beside the
// original (api/thumbnails.py). Originals are up to 2000px and a few hundred KB,
// and every avatar in the app is 64px or smaller — so rendering originals meant
// pulling tens of megabytes to draw a screenful of circles.
//
// `displayPx` is the CSS size the image is drawn at, multiplied here by the
// device pixel ratio, because a 32px avatar on a 3x phone needs 96 real pixels.
// The smallest rendition that covers that wins; when none does — an image
// uploaded before the pipeline existed, or one whose renditions failed to build
// — the answer is the original, which is exactly what the app did before.
export function pickRendition(thumbs, originalPath, displayPx) {
  if (!thumbs || !displayPx) return originalPath || null;
  const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
  const needed = displayPx * dpr;
  const candidates = Object.keys(thumbs)
    .map(Number)
    .filter(size => Number.isFinite(size) && thumbs[String(size)])
    .sort((a, b) => a - b);
  const chosen = candidates.find(size => size >= needed);
  return chosen ? thumbs[String(chosen)] : (originalPath || null);
}

// Resolves the best URL for an image a document describes, at the size it is
// drawn at.
//
// Three cases, and only the first costs a network round trip beyond the image
// itself:
//   - the original is what covers the size  → signed URL
//   - a rendition covers it and has a token → public capability URL, no call
//   - a rendition covers it and has none    → signed URL for the rendition
//
// The third is not a fallback so much as the state every image uploaded before
// commit 13 is in: the paths were recorded, the token was not. It behaves
// exactly as it did before, and the token backfill is what moves those records
// into the second case.
//
// `signOriginal` exists for a caller that already holds a signed URL for the
// original — the attachment row wants a rendition to draw and the original to
// open, and signing it twice would be two calls for one object.
export function useImageUrl(originalPath, thumbs, token, displayPx, signOriginal = true) {
  const picked = originalPath ? pickRendition(thumbs, originalPath, displayPx) : null;
  const isRendition = !!picked && picked !== originalPath;
  const direct = isRendition ? renditionUrl(picked, token) : null;
  const signed = useSignedUrl(
    picked && !direct && (isRendition || signOriginal) ? picked : null
  );
  return direct || signed;
}

// For person photos: resolves via the rendition or a signed URL if
// photoStoragePath is set, otherwise falls back to the legacy photoURL token
// (existing records).
//
// `displayPx` is optional so a caller that genuinely wants the full-size image
// can omit it; every avatar should pass the size it renders at.
export function usePersonPhotoUrl(person, displayPx) {
  const url = useImageUrl(
    person?.photoStoragePath || null,
    person?.photoThumbs,
    person?.photoThumbToken,
    displayPx,
  );
  if (person?.photoStoragePath) return url;
  return person?.photoURL || null;
}

export function useSignedUrl(storagePath) {
  const [url, setUrl] = useState(() => {
    if (!storagePath) return null;
    const cached = cache.get(storagePath);
    return cached && Date.now() < cached.expiresAt ? cached.url : null;
  });

  useEffect(() => {
    if (!storagePath) { setUrl(null); return; }
    const cached = cache.get(storagePath);
    if (cached && Date.now() < cached.expiresAt) { setUrl(cached.url); return; }
    let cancelled = false;
    getSignedUrl(storagePath)
      .then(u => { if (!cancelled) setUrl(u); })
      .catch(err => { console.error('[signedUrls] signing failed:', storagePath, err); });
    return () => { cancelled = true; };
  }, [storagePath]);

  return url;
}
